/**
 * The reader's box, as HeyDex's runtime worker reaches it.
 *
 * The box lives beside the page, not in the runtime worker: on the website and
 * in Android's web view it is truman's browser library (`WebTruman`) on the
 * page's main thread, and in the iOS app it is TrumanKit, which the page reaches
 * through the app's bridge. The page serves one `BoxHost` to the runtime worker
 * over a `MessagePort` (`serveBox`), and the worker calls it through
 * `connectBox`. Paths are absolute box paths (`/workspace/...`, `/root/...`).
 *
 * Commands run one at a time in call order, file operations included, so a long
 * command holds every later call until it ends or `interrupt` stops it.
 */

export interface BoxExecOptions {
  /** Where the command starts. Default `/workspace`. */
  readonly cwd?: string;
  /** Variables added to the box's environment for this command. */
  readonly env?: Readonly<Record<string, string>>;
  readonly stdin?: string;
  /** The command's deadline; past it the command ends with exit status 124. */
  readonly deadlineMs?: number;
}

export interface BoxExecResult {
  readonly stdout: string;
  readonly stderr: string;
  readonly exitCode: number;
  /** The working directory the command ended in. */
  readonly cwd: string;
  /** The environment the command ended with. */
  readonly env: Readonly<Record<string, string>>;
  /** Stopped at its deadline (exit status 124). */
  readonly timedOut: boolean;
  /** Output beyond 4 MiB per stream was cut. */
  readonly truncated: boolean;
  /** The box's page or worker ended while the command ran: it did not finish, and what it did is unknown. */
  readonly lost: boolean;
}

export interface BoxStat {
  readonly isFile: boolean;
  readonly isDirectory: boolean;
  readonly isSymbolicLink: boolean;
  readonly size: number;
  readonly mode: number;
  readonly mtimeMs: number;
}

export interface BoxEntry {
  readonly name: string;
  readonly isFile: boolean;
  readonly isDirectory: boolean;
  readonly isSymbolicLink: boolean;
}

/**
 * Why a box call failed. File-system codes are POSIX's; `lost` means the box's
 * page ended mid-call (its effect is unknown, so it is never retried);
 * `unavailable` means the box could not start.
 */
export type BoxErrorCode =
  | "ENOENT"
  | "EEXIST"
  | "ENOTDIR"
  | "EISDIR"
  | "ENOTEMPTY"
  | "EACCES"
  | "EINVAL"
  | "lost"
  | "unavailable"
  | "failed";

const ERROR_CODES: ReadonlySet<string> = new Set<BoxErrorCode>([
  "ENOENT",
  "EEXIST",
  "ENOTDIR",
  "EISDIR",
  "ENOTEMPTY",
  "EACCES",
  "EINVAL",
  "lost",
  "unavailable",
  "failed",
]);

export class BoxError extends Error {
  readonly code: BoxErrorCode;
  constructor(code: BoxErrorCode, message: string) {
    super(message);
    this.name = "BoxError";
    this.code = code;
  }
}

/** Decodes a code a box implementation attached to its error; anything else is `failed`. */
export function boxErrorCode(value: unknown): BoxErrorCode {
  return typeof value === "string" && ERROR_CODES.has(value) ? (value as BoxErrorCode) : "failed";
}

export interface BoxHost {
  exec(command: string, options?: BoxExecOptions): Promise<BoxExecResult>;
  /** Ends the running command the way Ctrl-C does. */
  interrupt(): Promise<void>;
  readFile(path: string): Promise<Uint8Array>;
  /** Creates the file's parent directories. */
  writeFile(path: string, content: Uint8Array): Promise<void>;
  stat(path: string): Promise<BoxStat>;
  listDir(path: string): Promise<readonly BoxEntry[]>;
  mkdir(path: string, options: { readonly recursive: boolean }): Promise<void>;
  remove(path: string, options: { readonly recursive: boolean; readonly force: boolean }): Promise<void>;
  move(from: string, to: string): Promise<void>;
}

type Method = keyof BoxHost;

type BoxCall = {
  [M in Method]: { readonly id: number; readonly method: M; readonly args: Parameters<BoxHost[M]> };
}[Method];

type BoxReply =
  | { readonly id: number; readonly ok: true; readonly value: unknown }
  | { readonly id: number; readonly ok: false; readonly code: BoxErrorCode; readonly message: string };

const METHODS: ReadonlySet<string> = new Set<Method>([
  "exec",
  "interrupt",
  "readFile",
  "writeFile",
  "stat",
  "listDir",
  "mkdir",
  "remove",
  "move",
]);

function isCall(value: unknown): value is BoxCall {
  if (typeof value !== "object" || value === null) return false;
  const call = value as Record<string, unknown>;
  return typeof call.id === "number" && typeof call.method === "string" && METHODS.has(call.method) && Array.isArray(call.args);
}

/** Serves `host` to the runtime worker on `port`. Returns a function that stops serving. */
export function serveBox(port: MessagePort, host: BoxHost): () => void {
  const onMessage = (event: MessageEvent<unknown>) => {
    const call = event.data;
    if (!isCall(call)) throw new Error("box: a malformed call reached the page");
    const run = (host[call.method] as (...args: readonly unknown[]) => Promise<unknown>).apply(host, call.args);
    run.then(
      (value) => {
        const reply: BoxReply = { id: call.id, ok: true, value };
        port.postMessage(reply, value instanceof Uint8Array ? [value.buffer] : []);
      },
      (error: unknown) => {
        const reply: BoxReply = {
          id: call.id,
          ok: false,
          code: error instanceof BoxError ? error.code : boxErrorCode((error as { code?: unknown } | null)?.code),
          message: error instanceof Error ? error.message : String(error),
        };
        port.postMessage(reply);
      },
    );
  };
  port.addEventListener("message", onMessage);
  port.start();
  return () => {
    port.removeEventListener("message", onMessage);
    port.close();
  };
}

/** The box the page serves on `port`, for the runtime worker. */
export function connectBox(port: MessagePort): BoxHost {
  let next = 0;
  const pending = new Map<number, { resolve: (value: unknown) => void; reject: (error: BoxError) => void }>();
  port.addEventListener("message", (event: MessageEvent<BoxReply>) => {
    const reply = event.data;
    const waiter = pending.get(reply.id);
    if (!waiter) throw new Error(`box: a reply to unknown call ${reply.id}`);
    pending.delete(reply.id);
    if (reply.ok) waiter.resolve(reply.value);
    else waiter.reject(new BoxError(reply.code, reply.message));
  });
  port.start();
  const call = <M extends Method>(method: M, args: Parameters<BoxHost[M]>, transfer: Transferable[] = []) =>
    new Promise<Awaited<ReturnType<BoxHost[M]>>>((resolve, reject) => {
      const id = next++;
      pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
      port.postMessage({ id, method, args } as BoxCall, transfer);
    });
  return {
    exec: (command, options) => call("exec", [command, options]),
    interrupt: () => call("interrupt", []),
    readFile: (path) => call("readFile", [path]),
    writeFile: (path, content) => call("writeFile", [path, content]),
    stat: (path) => call("stat", [path]),
    listDir: (path) => call("listDir", [path]),
    mkdir: (path, options) => call("mkdir", [path, options]),
    remove: (path, options) => call("remove", [path, options]),
    move: (from, to) => call("move", [from, to]),
  };
}
